import router from "@/router"
import { useUserStore } from "@/stores/user"
import type { NavigationItem } from "@/types/navigationItem"

import linkToCoachSelection from "@/composables/linkToCoachSelection"
import { GlobalVariables } from "@/helpers/globalVariables"

// STYLES
import { bittersweet600 } from "@/styles/colors"

// ASSETS
import iconGoogle from "@/assets/icons/icon-google-color.svg"
import iconCareersActive from "@/assets/menu/icon-careers-active.svg"
import iconCareers from "@/assets/menu/icon-careers.svg"
import iconCoachingActive from "@/assets/menu/icon-coaching-active.svg"
import iconCoaching from "@/assets/menu/icon-coaching.svg"
import iconFeatureRequest from "@/assets/menu/icon-feature-request.svg"
import iconGetNewCareersActive from "@/assets/menu/icon-get-new-careers-active.svg"
import iconGetNewCareers from "@/assets/menu/icon-get-new-careers.svg"
import iconHomeActive from "@/assets/menu/icon-home-active.svg"
import iconHome from "@/assets/menu/icon-home.svg"
import iconLogout from "@/assets/menu/icon-logout.svg"
import iconPersonalityActive from "@/assets/menu/icon-personality-active.svg"
import iconPersonality from "@/assets/menu/icon-personality.svg"
import iconReportABug from "@/assets/menu/icon-report-a-bug.svg"
import iconResourcesActive from "@/assets/menu/icon-resources-active.svg"
import iconResources from "@/assets/menu/icon-resources.svg"
import iconRocket from "@/assets/menu/icon-rocket.svg"
import iconAccount from "@/assets/menu/icon-profile.svg"
import iconAccountActive from "@/assets/menu/icon-profile-active.svg"
import iconCertificate from "@/assets/menu/icon-certificate.svg"

// SERVICES
import api from "@/services/apiClient"

export const navigationItems = (openModalPayment = () => {}) => {
  //STORES
  const userStore = useUserStore()

  return {
    coreItems: [
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.home.routeName })
        },
        icons: {
          default: iconHome,
          active: iconHomeActive
        },
        text: "Home",
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.home.routeName
      },
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.personality.routeName })
        },
        icons: {
          default: iconPersonality,
          active: iconPersonalityActive
        },
        text: "Personality",
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.personality.routeName
      },
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.careersOverview.routeName })
        },
        icons: {
          default: iconCareers,
          active: iconCareersActive
        },
        text: "Careers",
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.careersOverview.routeName
      },
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.resources.routeName })
        },
        icons: {
          default: iconResources,
          active: iconResourcesActive
        },
        text: "Resources",
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.resources.routeName
      },
      {
        action:
          !userStore.isCoachingHasEngaged && userStore.isActiveReportSubscriber
            ? () => {
                router.push({ name: GlobalVariables.urls.report.children.groupCoaching.routeName })
              }
            : linkToCoachSelection,
        icons: {
          default: iconCoaching,
          active: iconCoachingActive
        },
        text: "Coaching",
        isActive: [
          GlobalVariables.urls.coaching.children.selectCoach.routeName,
          GlobalVariables.urls.coaching.children.selectPlan.routeName,
          GlobalVariables.urls.coaching.children.chooseCoach.routeName,
          GlobalVariables.urls.coaching.children.choosePlan.routeName,
          GlobalVariables.urls.coaching.children.home.routeName,
          GlobalVariables.urls.report.children.groupCoaching.routeName
        ].includes(router.currentRoute.value.name?.toString() || "")
      }
    ],
    profileItems: [
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.account.routeName })
        },
        icons: {
          default: iconAccount,
          active: iconAccountActive
        },
        text: "Account",
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.account.routeName
      },
      {
        action: () => openModalPayment(),
        icons: {
          default: iconRocket
        },
        text: "Upgrade",
        isHidden:
          !userStore.isReportUser ||
          userStore.isTierComprehensiveOrHigher ||
          [
            GlobalVariables.urls.coaching.children.selectCoach.routeName,
            GlobalVariables.urls.coaching.children.selectPlan.routeName,
            GlobalVariables.urls.coaching.children.chooseCoach.routeName,
            GlobalVariables.urls.coaching.children.choosePlan.routeName,
            GlobalVariables.urls.coaching.children.confirmation.routeName,
            GlobalVariables.urls.coaching.children.confirmed.routeName,
            GlobalVariables.urls.coaching.children.cart.routeName
          ].includes(router.currentRoute.value.name?.toString() || "")
      },
      {
        action: () => {
          api.auth.goToGoogleOauth(
            window.location.pathname + window.location.search, // redirect after successfully linking
            window.location.pathname + window.location.search // redirect after failure
          )
        },
        icons: {
          default: iconGoogle
        },
        text: "Connect to Google",
        isHidden: userStore.isGoogleLinked
      },
      {
        action: () =>
          api.auth
            .postLogoutApp()
            .then(() => {
              window.location.href = GlobalVariables.urls.login.path
            })
            .catch((error) => {
              console.error("Error logging out", error)
            }),
        icons: {
          default: iconLogout
        },
        text: "Logout",
        textColor: bittersweet600
      }
    ],
    supportItems: [
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.newCareers.routeName })
        },
        icons: {
          default: iconGetNewCareers,
          active: iconGetNewCareersActive
        },
        text: "Get New Careers",
        isHidden: !userStore.isTierAnyPaid,
        isActive: router.currentRoute.value.name === GlobalVariables.urls.report.children.newCareers.routeName
      },
      {
        action: () => {
          router.push({ name: GlobalVariables.urls.report.children.testRetake.routeName })
        },
        icons: {
          default: iconCertificate
        },
        text: "Take Career Test",
        isHidden: userStore.isReportUser || !userStore.isCoachingHasEngaged
      },
      {
        action: linkToCoachSelection,
        icons: {
          default: iconCoaching,
          active: iconCoachingActive
        },
        text: "Select Coach",
        isHidden: userStore.isCoachingHasEngaged || userStore.isReportUser || userStore.isExecutiveCoachingPotential,
        isActive: [GlobalVariables.urls.coaching.children.selectCoach.routeName].includes(
          router.currentRoute.value.name?.toString() || ""
        )
      },
      {
        action: () => {
          window.open("https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2uNSktDWV6KUvui1R")
        },
        icons: {
          default: iconFeatureRequest
        },
        text: "Feature Request",
        isHidden: !userStore.isReportUser || !userStore.isTierFreeOrHigher
      },
      {
        action: () => {
          window.open("https://survey.zigpoll.com/2tVLrZ1upJBnDCLQ3/2ue38doMHvnsYd8Ga")
        },
        icons: {
          default: iconReportABug
        },
        text: "Report a Bug"
      }
    ]
  } as Record<string, NavigationItem[]>
}

export default navigationItems
